import { forwardRef, useId, useImperativeHandle, useRef, type KeyboardEvent } from 'react';

import { FIELD_SLOT_CLASSES } from '@/shared/components/ui/field/field.tokens';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/ui/Select/Select';
import { cn } from '@/shared/utils/cn/cn.util';

import { isHeightUnit } from '../height-scale/height-scale.util';
import { ScalarFieldIsland } from '../ScalarFieldIsland/ScalarFieldIsland';
import { ScalarHint } from '../ScalarHint/ScalarHint';
import { ScalarShell } from '../ScalarShell/ScalarShell';
import {
  DURATION_UNIT_CLASSES,
  HEIGHT_AMOUNT_CLASSES,
  HEIGHT_INCHES_CLASSES,
  HEIGHT_PAIR_CLASSES,
  HEIGHT_PART_MARK_CLASSES,
} from '../scalar.tokens';
import { type UseScalarNumber } from '../useScalarNumber/useScalarNumber';
import { type ScalarHandle } from '../types/scalar-field.types';

import { type HeightInputProps } from './HeightInput.types';
import { useHeightInputLogic } from './useHeightInputLogic';

/** What one press of each key is worth, as a multiple of the step. */
const KEY_STEPS: Readonly<Record<string, number>> = {
  ArrowUp: 1,
  ArrowDown: -1,
  PageUp: 10,
  PageDown: -10,
};

/**
 * A member's height — stored in centimetres, read in whichever unit they think
 * in.
 *
 * **Self-naming.** It renders two or three named controls (an amount or a
 * feet/inches pair, plus the unit chooser), so the group carries the field's
 * name and each child a composed one. A single `<Label htmlFor>` could only ever
 * point at one of them.
 *
 * The description is threaded onto each CONTROL rather than onto the group: a
 * group's description is not reliably announced on entry, so an operator who
 * tabs straight into the amount would never hear it — the note `DurationInput`
 * and `ColorInput` both carry.
 */
export const HeightInput = forwardRef<ScalarHandle, HeightInputProps>(function HeightInput(
  {
    id,
    className,
    size,
    label,
    value,
    onValueChange,
    min,
    max,
    isDisabled = false,
    isReadOnly = false,
    isInvalid = false,
    onBlur,
    'aria-describedby': describedBy,
  },
  ref,
) {
  const generatedId = useId();
  const fieldId = id ?? generatedId;
  const hintId = `${fieldId}-hint`;
  const inputRef = useRef<HTMLInputElement | null>(null);

  const view = useHeightInputLogic({
    label,
    value,
    onValueChange,
    min,
    max,
    isDisabled,
    isReadOnly,
  });

  useImperativeHandle(ref, () => ({
    focus: () => inputRef.current?.focus(),
    get element() {
      return inputRef.current;
    },
  }));

  const describe = cn(describedBy, hintId);
  const isImperial = view.unit === 'ftIn';

  /** One numeric slot — the centimetre box, or one half of the imperial pair. */
  const amountBox = (
    slot: UseScalarNumber,
    options: {
      id?: string;
      controlLabel: string;
      valueMin: number | undefined;
      valueMax: number | undefined;
      isPrimary: boolean;
      /** Overrides the slot's share of the island — the imperial halves do. */
      slotClasses?: string;
      /** In characters. The inches box holds two digits and is sized to say so. */
      size?: number;
    },
  ) => (
    <input
      ref={options.isPrimary ? inputRef : undefined}
      id={options.id}
      // `text` + `inputMode`, not `type="number"`: a number input reports an
      // EMPTY value for an in-progress draft, so a refused fraction — the whole
      // behaviour this control is built around — could never be shown back.
      // Spinbutton semantics are supplied explicitly below.
      type="text"
      inputMode="numeric"
      role="spinbutton"
      aria-label={options.controlLabel}
      aria-describedby={describe}
      aria-valuenow={slot.draftValue}
      aria-valuemin={options.valueMin}
      aria-valuemax={options.valueMax}
      aria-valuetext={view.storedHint}
      aria-invalid={isInvalid || slot.isDraftInvalid ? true : undefined}
      aria-readonly={isReadOnly ? true : undefined}
      value={slot.text}
      disabled={isDisabled}
      readOnly={isReadOnly}
      autoComplete="off"
      size={options.size}
      onChange={(event) => {
        slot.setText(event.target.value);
      }}
      onKeyDown={(event: KeyboardEvent<HTMLInputElement>) => {
        const steps = KEY_STEPS[event.key];
        if (steps === undefined) return;
        event.preventDefault();
        slot.stepBy(event.shiftKey ? steps * 10 : steps);
      }}
      onBlur={() => {
        onBlur?.();
      }}
      className={cn(FIELD_SLOT_CLASSES, options.slotClasses ?? HEIGHT_AMOUNT_CLASSES)}
    />
  );

  return (
    <ScalarShell
      role="group"
      className={className}
      label={label}
      aria-describedby={describe}
      isReadOnly={isReadOnly}
      announcement={view.announcement}
      hint={
        <ScalarHint id={hintId} range={view.rangeHint} warning={view.warning}>
          {view.storedHint}
        </ScalarHint>
      }
    >
      <ScalarFieldIsland
        size={size}
        isInvalid={
          isInvalid ||
          (isImperial
            ? view.feet.isDraftInvalid || view.inches.isDraftInvalid
            : view.cm.isDraftInvalid)
        }
        adornment={
          <Select
            value={view.unit}
            onValueChange={(next) => {
              // Narrowed rather than cast: an unknown unit has no conversion,
              // and `as HeightUnit` would hand one to the scale anyway.
              if (isHeightUnit(next)) view.onUnitChange(next);
            }}
            disabled={isDisabled}
          >
            <SelectTrigger
              variant="slot"
              className={DURATION_UNIT_CLASSES}
              aria-label={view.unitLabel}
              aria-describedby={describe}
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {view.units.map((unit) => (
                <SelectItem key={unit} value={unit}>
                  {view.unitName(unit)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        }
      >
        {isImperial ? (
          <>
            {amountBox(view.feet, {
              // The caller's `id` follows the PRIMARY box, whichever unit is on
              // screen: an id that exists in centimetres and vanishes in feet is
              // an `aria-controls` or a `getElementById` that resolves to
              // nothing half the time.
              id: fieldId,
              controlLabel: view.feetLabel,
              valueMin: 0,
              valueMax: undefined,
              isPrimary: true,
              slotClasses: HEIGHT_PAIR_CLASSES,
            })}
            {/*
              The prime marks, not a neutral separator. `5 · 8` is two numbers
              and a dot — the first still of this control showed exactly that,
              and nothing on screen said which half was feet. `′`/`″` are the
              typographic standard, cost a character each (the pair has least
              room at 320), and stay `aria-hidden`: each box already carries a
              composed name, and reading "prime" aloud would be noise.
            */}
            <span aria-hidden className={HEIGHT_PART_MARK_CLASSES}>
              ′
            </span>
            {amountBox(view.inches, {
              controlLabel: view.inchesLabel,
              valueMin: 0,
              valueMax: 11,
              isPrimary: false,
              slotClasses: HEIGHT_INCHES_CLASSES,
              size: 2,
            })}
            <span aria-hidden className={HEIGHT_PART_MARK_CLASSES}>
              ″
            </span>
          </>
        ) : (
          amountBox(view.cm, {
            id: fieldId,
            controlLabel: view.amountLabel,
            // The CLAMPED bounds, never the raw props: assistive tech must be
            // told the range the api will actually accept.
            valueMin: view.minCm,
            valueMax: view.maxCm,
            isPrimary: true,
          })
        )}
      </ScalarFieldIsland>
    </ScalarShell>
  );
});
